import React from 'react';
import styled from 'styled-components/macro';
import {
  Div,
  PdfToolbar as Toolbar,
  Wrapper,
  PdfButton,
  Center,
  Left,
  Right,
  InputContainer,
  PageInput,
  NumPages,
  VerticalBar
} from './newPdf.style';
import Icon from 'components/Input/Icon';
import Spinner from 'components/Spinner/Spinner';
import utils from 'utils';

const PlaceholderCanvas = styled.div<{ height?: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colorPalette.grey.grey1};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colorPalette.grey.grey2};
  }

  &:disabled,
  &[data-loading="true"] {
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const IconWrapper = styled.div`
  font-size: 48px;
  color: ${({ theme }) => theme.colorPalette.grey.grey6};
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-family: Roboto;
  font-weight: ${({ theme }) => theme.fontWeightPalette.medium};
  font-size: ${({ theme }) => theme.fontSizePalette.large};
  color: ${({ theme }) => theme.colorPalette.grey.grey9};
  margin: 0 0 8px 0;
  text-align: center;
`;

const Description = styled.p`
  font-family: Roboto;
  font-weight: ${({ theme }) => theme.fontWeightPalette.light};
  font-size: ${({ theme }) => theme.fontSizePalette.medium};
  color: ${({ theme }) => theme.colorPalette.grey.grey7};
  margin: 0;
  text-align: center;
  max-width: 300px;
  line-height: 1.4;
`;

interface PdfPlaceholderProps {
  onLoadPdf: () => void;
  isLoading: boolean;
  height?: string;
  title?: string;
  description?: string;
  showFitButtons?: boolean;
  showRotateButtons?: boolean;
  showPageNavigation?: boolean;
  showZoomButtons?: boolean;
  showDownloadButton?: boolean;
  showOcrButtons?: boolean;
  showMappingButtons?: boolean;
  showTranslateButtons?: boolean;
}

const PdfPlaceholder: React.FC<PdfPlaceholderProps> = ({
  onLoadPdf,
  isLoading,
  height,
  title = 'PDF Preview',
  description = 'Click here to load the PDF preview. This will improve page loading performance.',
  showFitButtons = true,
  showRotateButtons = true,
  showPageNavigation = true,
  showZoomButtons = true,
  showDownloadButton = true,
  showOcrButtons = true,
  showMappingButtons = false,
  showTranslateButtons = true,
}) => {
  const t = utils.intl.useTranslator()
  return (
    <Div>
      {/* Disabled PDF Toolbar */}
      <Toolbar>
        <Left>
          {showFitButtons && (
            <PdfButton disabled={true}>
              <Icon icon="arrow-alt-v" rotate={90} size="xl" disabled={true} />
            </PdfButton>
          )}
        </Left>
        <Center>
          {showRotateButtons && (
            <>
              <PdfButton disabled={true}>
                <Icon icon="undo-alt" size="xl" disabled={true} />
              </PdfButton>
              <PdfButton disabled={true}>
                <Icon icon="redo-alt" size="xl" disabled={true} />
              </PdfButton>
              <VerticalBar />
            </>
          )}
          {showPageNavigation && (
            <>
              <Icon icon="top-page" disabled={true} />
              <PdfButton disabled={true}>
                <Icon icon="angle-up" size="xl" disabled={true} />
              </PdfButton>
              <InputContainer>
                <PageInput value="--" disabled />
                <NumPages>/--</NumPages>
              </InputContainer>
              <PdfButton disabled={true}>
                <Icon icon="angle-up" rotate={180} size="xl" disabled={true} />
              </PdfButton>
              <Icon icon="top-page" style={{ transform: 'rotate(180deg)' }} disabled={true} />
              <VerticalBar />
            </>
          )}
          {showZoomButtons && (
            <>
              <PdfButton disabled={true}>
                <Icon icon="searchMinus" size="xl" disabled={true} />
              </PdfButton>
              <PdfButton disabled={true}>
                <Icon icon="search-plus" size="xl" disabled={true} />
              </PdfButton>
            </>
          )}
        </Center>
        <Right>
          {showOcrButtons && (
            <>
              <PdfButton disabled={true}>
                <Icon icon="ocr" size="xl" disabled={true} />
              </PdfButton>
              <PdfButton disabled={true}>
                <Icon icon="ocr-area" size="xl" disabled={true} />
              </PdfButton>
              <PdfButton disabled={true}>
                <Icon icon="ocr-click" size="xl" disabled={true} />
              </PdfButton>
            </>
          )}
          {showMappingButtons && (
            <PdfButton disabled={true}>
              <Icon icon="object-ungroup" size="xl" disabled={true} />
            </PdfButton>
          )}
          {showDownloadButton && (
            <>
              <VerticalBar />
              <PdfButton disabled={true}>
                <Icon icon="file-pdf" size="xl" disabled={true} />
              </PdfButton>
            </>
          )}
          {showTranslateButtons && (
            <PdfButton disabled={true}>
              <Icon icon="globe" size="xl" disabled={true} />
            </PdfButton>
          )}
        </Right>
      </Toolbar>

      {/* Clickable Canvas Area */}
      <Wrapper fixedHeight={height}>
        <PlaceholderCanvas
          onClick={!isLoading ? onLoadPdf : undefined}
          height={height}
          data-loading={isLoading}
        >
          <IconWrapper>
            {isLoading ? <Spinner size={48} /> : <Icon icon="file" size={48} />}
          </IconWrapper>
          <Title>
            {isLoading ? t('Loading-PDF') : t(title)}
          </Title>
          <Description>
            {isLoading ? t('document-loads-message') : t(description)}
          </Description>
        </PlaceholderCanvas>
      </Wrapper>
    </Div>
  );
};
PdfPlaceholder.displayName = 'PdfPlaceholder';
export default PdfPlaceholder;
